/**
 * Where a certificate comes from, and how the system gets to it.
 *
 * Two different questions, and the operational panel needs both. The **source**
 * is the body that issues the document — a court, a city hall, a registry hub.
 * The **strategy** is how we reach it: a public API, a credentialed hub, a
 * robot driving a portal, a paid provider, or a person.
 *
 * The distinction is not academic: the strategy decides what a failure means
 * and who fixes it. A free official API that fails is the agency being down —
 * wait. A state RPA that fails is a portal that changed its layout — patch the
 * scraper, times twenty-seven instances. A private provider that fails is
 * credit running out — top up the contract. Reporting a single "success rate
 * per source" hides all of that behind one number.
 */

/**
 * How the system reaches a source — and, with it, what a failure means.
 *
 * Ordered from cheapest to reach to most expensive to keep, which is the order
 * the panel reads best in.
 */
export const INTEGRATION_STRATEGIES = [
  'freeApi',
  'registryHub',
  'federalRpa',
  'stateRpa',
  'cityRpa',
  'privateProvider',
  'manual'
] as const

export type IntegrationStrategy = (typeof INTEGRATION_STRATEGIES)[number]

/** The vocabulary of the diligence matrix, kept in the words the team uses. */
export const STRATEGY_LABEL: Record<IntegrationStrategy, string> = {
  freeApi: 'API oficial gratuita',
  registryHub: 'Central cartorária',
  federalRpa: 'RPA federal',
  stateRpa: 'RPA por UF',
  cityRpa: 'RPA por município',
  privateProvider: 'Provedor privado',
  manual: 'Manual'
}

/**
 * What breaks first in each strategy.
 *
 * The panel shows it next to the failure count so whoever is on duty reads the
 * number and the likely cause in the same glance.
 */
export const STRATEGY_TYPICAL_FAILURE: Record<IntegrationStrategy, string> = {
  freeApi: 'indisponibilidade do órgão',
  registryHub: 'credenciamento ou emolumento',
  federalRpa: 'mudança de layout do portal',
  stateRpa: 'mudança de layout, em 27 instâncias',
  cityRpa: 'portal inexistente no município',
  privateProvider: 'saldo ou limite de contrato',
  manual: 'SLA humano'
}

/** The bodies the catalogue pulls documents from. */
export const SOURCE_KEYS = [
  'onr',
  'serasa',
  'brasilApi',
  'cgu',
  'rfb',
  'trf',
  'tst',
  'trt',
  'stf',
  'stj',
  'caixa',
  'bacen',
  'ibama',
  'sicar',
  'tj',
  'sefaz',
  'ssp',
  'mp',
  'crea',
  'cetesb',
  'cityHall',
  'condominium',
  'client',
  /**
   * A source the product has not integrated.
   *
   * Used by the certificates the matrix demands and no scraper fetches: the
   * catalogue names the issuing body in prose, and nobody has turned it into a
   * robot yet. It is the honest answer, and it keeps those certificates out of
   * the per-source success rates, where they would look like failures.
   */
  'catalogue'
] as const

export type SourceKey = (typeof SOURCE_KEYS)[number]

export type Source = {
  label: string
  /** How an automated search reaches it. Manual items answer to no portal. */
  strategy: Exclude<IntegrationStrategy, 'manual'> | 'manual'
}

export const SOURCES: Record<SourceKey, Source> = {
  onr: { label: 'ONR / Registradores', strategy: 'registryHub' },
  serasa: { label: 'Serasa Experian', strategy: 'privateProvider' },
  brasilApi: { label: 'BrasilAPI (Receita Federal)', strategy: 'freeApi' },
  cgu: { label: 'CGU — Portal da Transparência', strategy: 'freeApi' },
  rfb: { label: 'Receita Federal / PGFN', strategy: 'federalRpa' },
  trf: { label: 'Justiça Federal (TRFs)', strategy: 'federalRpa' },
  tst: { label: 'TST', strategy: 'federalRpa' },
  trt: { label: 'TRTs', strategy: 'federalRpa' },
  stf: { label: 'STF', strategy: 'federalRpa' },
  stj: { label: 'STJ', strategy: 'federalRpa' },
  caixa: { label: 'Caixa Econômica Federal', strategy: 'federalRpa' },
  bacen: { label: 'Banco Central', strategy: 'federalRpa' },
  ibama: { label: 'IBAMA', strategy: 'federalRpa' },
  sicar: { label: 'SICAR', strategy: 'federalRpa' },
  tj: { label: 'Tribunais de Justiça', strategy: 'stateRpa' },
  sefaz: { label: 'SEFAZ / PGE', strategy: 'stateRpa' },
  ssp: { label: 'Secretaria de Segurança Pública', strategy: 'stateRpa' },
  mp: { label: 'Ministério Público', strategy: 'stateRpa' },
  crea: { label: 'CREA', strategy: 'stateRpa' },
  cetesb: { label: 'CETESB', strategy: 'stateRpa' },
  cityHall: { label: 'Prefeituras', strategy: 'cityRpa' },
  condominium: { label: 'Condomínio / administradora', strategy: 'manual' },
  client: { label: 'Documentos do cliente', strategy: 'manual' },
  catalogue: { label: 'Órgão sem integração (ver catálogo)', strategy: 'manual' }
}

/**
 * The strategy in force for one item of the catalogue.
 *
 * What has no scraper is a human queue, whatever the body behind it — that is
 * why `automated` decides before the source does. A city hall with a robot is
 * a municipal RPA; the same city hall without one is manual work, and it fails
 * for entirely different reasons.
 */
export const strategyOf = (template: { source: SourceKey; automated: boolean }): IntegrationStrategy =>
  template.automated ? SOURCES[template.source].strategy : 'manual'
